import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';

// import { t } from 'i18next';
import axios from 'axios';

import styles from './JobsDetail.module.css';
import BubbleButton from '../../../Buttons/BubbleButton/BubbleButton';
import Button from '../../../Buttons/Button/Button';
import BasicPopup from '../../../Popups/BasicPopup/BasicPopup';
import FormRichTextField from '../../../FormUI/FormRichTextField/FormRichTextField';
import Yup from '../../../../../utils/yupExtensions';
import { useState, useRef, useCallback, useEffect } from 'react';
import Flag from 'react-flags';
// import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { getCurrencySymbol, getTimeAgo } from '../../../../../utils/formatHelpers';
import { updateApplications, updateFavourites } from '../../../../../app/features/userSlice';
import { ExclusiveJobIcon, SaveBookMark, SaveIcon } from '../../../../../assets/images/icons';
import { useGetEducationLevelsHook, useGetJobCategoriesHook } from '../../../../../utils/utilityHooks';
import { addJobToFavourites, removeJobFromFavourites, sendJobProposal } from '../../../../../services/jobService';
import { useTranslation } from 'react-i18next';
import { FaInfoCircle, FaCheck } from 'react-icons/fa';

// Currency exchange rates
const EXCHANGE_RATES = {
    AED: 3.67, // 1 USD = 3.67 AED
    SAR: 3.75, // 1 USD = 3.75 SAR
    QAR: 3.64, // 1 USD = 3.64 QAR
    OMR: 0.38, // 1 USD = 0.38 OMR
    KWD: 0.31, // 1 USD = 0.31 KWD
    BHD: 0.38  // 1 USD = 0.38 BHD
};

// List of currency codes to display in tooltip
const CURRENCY_CODES = ['usd', 'aed', 'sar', 'qar', 'omr', 'kwd', 'bhd'];

function JobDetailSection({ job, selectedResume, agreeTermsAndCondition, cvPair, hasSkillMatch }) {
    const userToken = useSelector((state) => state.user.token);
    const user = useSelector((state) => state.user.user);
    const dispatch = useDispatch();
    const [formLoading, setFormLoading] = useState(false);
    const { t } = useTranslation();
    const [showTooltip, setShowTooltip] = useState(false);
    const tooltipRef = useRef(null);
    const tooltipTimerRef = useRef(null);
    const tooltipHoverRef = useRef(false);
    const [showAllRoleSkills, setShowAllRoleSkills] = useState(false);
    const [showAllSkills, setShowAllSkills] = useState(false);

    // Handle tooltip visibility
    const handleTooltipShow = useCallback(() => {
        tooltipHoverRef.current = true;
        if (tooltipTimerRef.current) {
            clearTimeout(tooltipTimerRef.current);
            tooltipTimerRef.current = null;
        }
        setShowTooltip(true);
    }, []);

    const handleTooltipHide = useCallback(() => {
        tooltipHoverRef.current = false;
        tooltipTimerRef.current = setTimeout(() => {
            if (!tooltipHoverRef.current) {
                setShowTooltip(false);
            }
        }, 300);
    }, []);

    // Clear timeout on unmount
    useEffect(() => {
        return () => {
            if (tooltipTimerRef.current) {
                clearTimeout(tooltipTimerRef.current);
            }
        };
    }, []);

    // Calculate salary in a specific currency
    const calculateSalary = useCallback((amount, currencyCode) => {
        if (currencyCode === 'USD') {
            return amount;
        }

        const rate = EXCHANGE_RATES[currencyCode];
        if (!rate) return amount;
        return amount * rate;
    }, []);

    // Format salary range for display
    const formatSalaryRange = useCallback((fromValue, toValue, symbol) => {
        if (fromValue === toValue) {
            return `${symbol}${fromValue}`;
        }
        return `${symbol}${fromValue} - ${symbol}${toValue}`;
    }, []);

    // Prepare tooltip content with all currency conversions
    const getSalaryInAllCurrencies = useCallback(() => {
        const salaryFrom = job.salary_low || 0;
        const salaryTo = job.salary_high || 0;

        const currencies = CURRENCY_CODES.map(code => ({
            code: code.toUpperCase(),
            symbol: getCurrencySymbol(code),
            name: code.toUpperCase()
        }));

        return (
            <div className={styles.currencyTooltip}>
                <p className={styles.tooltipTitle}>{t('jobs.salary_in_currencies')}</p>
                {currencies.map(curr => {
                    const fromValue = calculateSalary(salaryFrom, curr.code);
                    const toValue = calculateSalary(salaryTo, curr.code);

                    return (
                        <div key={curr.code} className={styles.currencyRow}>
                            <span className={styles.currencyName}>{curr.name}:</span>
                            <span className={styles.currencyValue}>
                                {formatSalaryRange(fromValue, toValue, curr.symbol)}
                            </span>
                        </div>
                    );
                })}
            </div>
        );
    }, [job.salary_low, job.salary_high, t, calculateSalary, formatSalaryRange]);

    // Check if job is accepting applications
    const isJobAcceptingApplications = job.status === 'opened' || job.status === 'published' || job.status === 'active';

    // Get status message based on job status
    const getStatusMessage = () => {
        switch (job.status) {
            case 'paused':
                return t('job_status_messages.paused');
            case 'closed':
                return t('job_status_messages.closed');
            case 'expired':
                return t('job_status_messages.expired');
            default:
                return t('job_status_messages.default');
        }
    };

    let city = job.city;
    if (t('general.city.' + city) !== 'general.city.' + city) {
        city = t('general.city.' + city);
    }

    const handleAddToFavourites = () => {
        addJobToFavourites({
            job_id: job.id,
            token: userToken,
        })
            .then(() => {
                dispatch(updateFavourites([...user.favourites, parseInt(job.id, 10)]));
                toast.success(t('job.job_added_to_favorites'));
            })
            .catch((error) => {
                if (error?.response?.data?.error) {
                    toast.error(error.response.data.error);
                }
            });
    };

    const handleRemoveFromFavourites = () => {
        removeJobFromFavourites({
            job_id: job.id,
            token: userToken,
        })
            .then(() => {
                dispatch(updateFavourites(user.favourites.filter((item) => item !== parseInt(job.id, 10))));
                toast.success(t('job.job_removed_from_favorites'));
            })
            .catch((error) => {
                if (error?.response?.data?.error) {
                    toast.error(error.response.data.error);
                }
            });
    };

    let date = '';
    if (job.createdAt) {
        const date_info = getTimeAgo(new Date(job.createdAt));
        date = t('general.UI.posted') + ' ';

        if (date_info.unit_plural === 'second' || date_info.unit_plural === 'seconds') {
            date += t('general.UI.just_now');
        }
        else {
            date += date_info.count + ' ' + t('general.UI.' + date_info.unit_plural) + ' ' + t('general.UI.ago');
        }

    }
    let salary = '';
    let salary_period = t('general.UI.month');

    if (job?.salaryrate) salary_period = t('CompensationAndbenefits.' + job?.salaryrate[0].toUpperCase() + job?.salaryrate.slice(1, job?.salaryrate.length));
    else if (job?.payment_type === 'hourly') salary_period = t('general.UI.hour');

    if (job?.currency && job?.currency !== 'undefined') {
        const currency = getCurrencySymbol(job.currency);
        if (job.salary_low !== job.salary_high) {
            salary = currency + job.salary_low + ' - ' + currency + job.salary_high;
        } else {
            salary = currency + job.salary_low;
        }
    } else {
        const currency = getCurrencySymbol('usd');
        salary = currency + job.salary_low + ' - ' + currency + job.salary_high;
    }

    const desc = job.description;
    const detailUI = (name, value) => (
        <div className={styles.row}>
            <p className={styles.detailTitle}>{name}</p>
            <p className={styles.detailTitleValue}>{value}</p>
        </div>
    );
    const [isSelectCVPopupOpen, setIsSelectCVPopupOpen] = useState(false);
    const [isApplyPopupOpen, setIsApplyPopupOpen] = useState(false);
    const navigate = useNavigate();
    const applyJob = async () => {
        try {
            if (formLoading) {
                return;
            }

            setFormLoading(true);

            const selectCVPair = cvPair.filter((e) => e.id === selectedResume);
            if (!selectCVPair.length) {
                toast.error(t('job.no_resume_selected'));
                setFormLoading(false);
                return;
            }

            let cvFilePath;
            let cvRefLetterPath;

            if (selectCVPair[0].cv_file instanceof File || selectCVPair[0].cv_ref_letter instanceof File) {
                const formData = new FormData();
                formData.append('token', userToken);
                formData.append('cv_file', selectCVPair[0].cv_file);
                formData.append('cv_ref_letter', selectCVPair[0].cv_ref_letter);

                try {
                    const response = await axios.post(
                        `${process.env.REACT_APP_API_URL}job/uploadCV`,
                        formData,
                        {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                            },
                        }
                    );

                    cvFilePath = response.data.cvFilePath;
                    cvRefLetterPath = response.data.cvRefLetterPath;
                } catch (error) {
                    toast.error(error.response?.data?.error || t('job.file_upload_failed'));
                    setFormLoading(false);
                    return;
                }
            }

            const proposalData = {
                job_id: job.id,
                token: userToken,
                status: "New"
            }

            if (selectCVPair.length > 0) {
                proposalData.proposal_cv = selectCVPair[0].cv_file?.path || cvFilePath;
                proposalData.proposal_cover_letter = selectCVPair[0]?.cv_ref_letter?.path || cvRefLetterPath;
            }

            toast.promise(sendJobProposal(proposalData), {
                loading: t('general.UI.please_wait'),
                success: <b>{t('general.UI.success')}</b>,
                error: (err) => {
                    return <b>{err.response?.data?.error || t('general.UI.error')}</b>;
                },
            })
                .then((response) => {
                    dispatch(updateApplications([...user.applications, parseInt(job.id, 10)]));
                    setFormLoading(false);
                    setIsApplyPopupOpen(false);
                })
                .catch((error) => {
                    setFormLoading(false);
                });
        } catch (error) {
            toast.error(error.message || t('general.UI.something_went_wrong'));
            setFormLoading(false);
        }
    }
    const handleApplyClick = async (e) => {
        e.preventDefault();

        // Check if job is accepting applications
        if (!isJobAcceptingApplications) {
            toast.error(getStatusMessage());
            return;
        }

        if (user) {
            if (agreeTermsAndCondition && hasSkillMatch) {
                if (user?.verification_status == 'approved') {
                    applyJob()
                } else {
                    applyJob()
                }
            } else {
                const target = document.querySelector('.formScroll');
                if (target) {
                    window.scrollTo({
                        top: target.offsetTop,
                        behavior: 'smooth'
                    });
                }
            }
        }
        else {
            navigate('/login');
        }

    };
    const handlePopupClose = () => {
        setIsSelectCVPopupOpen(false);
        setIsApplyPopupOpen(false);
    };
    const educationLevels = useGetEducationLevelsHook();
    const categoryOptions = useGetJobCategoriesHook();
    return (
        <div className={`${styles.container} jobDetail`}>
            <div className={styles.job_card_head_info} style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div className={styles.job_card_head_info_main} >
                    <h5 className={styles.jobTitle}>
                        {job.title}
                        {job.exlusiveJob && <img src={ExclusiveJobIcon} alt="Exclusive Job" />}
                    </h5>
                    {job.exlusiveJob &&
                        <div>
                            <p className={styles.exlusiveJobTitle}>{t("UploadResume.this_is_an_exclusive_job")}</p>
                        </div>
                    }
                </div>
                <div className={styles.applyButtonContainer}>
                    <p className={styles.postedDate} >{date}</p>

                    {user?.account_type === 'jobseeker' && (
                        user?.favourites?.includes(parseInt(job.id, 10)) ? (
                            <div className={styles.bookMarkContainer}>
                                <img
                                    onClick={handleRemoveFromFavourites}
                                    src={SaveBookMark}
                                    className={styles.bookMarkIcon}
                                    style={{ margin: 0 }}
                                    alt="Remove from favorites"
                                />
                            </div>
                        ) : (
                            <img
                                style={{ margin: 0 }}
                                onClick={handleAddToFavourites}
                                src={SaveIcon}
                                className={styles.bookMarkIcon}
                                alt="Add to favorites"
                            />
                        )
                    )}
                    {isJobAcceptingApplications && (user?.account_type !== 'employer' && !user?.applications?.includes(parseInt(job.id, 10))) && (
                        <>
                            {(job.exlusiveJob && user.verification_status !== 'approved') ?
                                <BubbleButton
                                    onClick={() => { navigate('/subscriptions'); }}
                                    dark
                                    headerDarkButton
                                    type="default"
                                    small
                                >
                                    {t('forms.verification.get_verified_to_apply')}
                                </BubbleButton>
                                :
                                <BubbleButton
                                    onClick={handleApplyClick}
                                    dark
                                    headerDarkButton
                                    type="default"
                                    small
                                >
                                    {t('general.UI.Apply_now')}
                                </BubbleButton>
                            }
                        </>
                    )}
                    {user?.applications?.includes(parseInt(job.id, 10)) && <strong style={{ color: '#34A853' }}>{t('general.UI.applied')}</strong>}

                </div>
            </div>
            <div className={styles.jobBody}>
                <h5 className={styles.jobTitle}>{t("forms.manage_job.subtitle_description")}</h5>
                {desc.trim().replace(/<[^>]*>/g, '').length > 0 && (
                    <div className={styles.job_card_footer}>
                        <div dangerouslySetInnerHTML={{ __html: desc }} className={styles.jobDescription}></div>
                    </div>
                )}
            </div>
            <div className={styles.jobDetail}>
                <h5 className={styles.jobTitle}>{t('forms.manage_job.job_details')}</h5>
                {/* Location with flag */}
                {detailUI(
                    t('forms.manage_job.location'),
                    <>
                        {/* {job.country && <span className={styles.countryFlag}>{job.country_flag}</span>} */}

                        <div className={styles.flagContainer} >
                            {job.country && <div style={{ cursor: 'pointer' }}>
                                <Flag
                                    name={job.country}
                                    format="svg"
                                    shiny={false}
                                    basePath="/vendor/flags"
                                />
                                <span>{t('general.country.' + job.country)}{job.city ? ', ' : ''}</span>
                                <span>{t('general.city.' + job.city)}</span>
                            </div>
                            }
                        </div>
                    </>
                )}

                {/* Location Type */}
                {detailUI(t('forms.manage_job.location_type'), t(`general.UI.${job.job_location}`))}

                {/* Job Category */}
                {detailUI(t('forms.manage_job.jobCategory'), categoryOptions.filter((e) => e?.value == job?.category)[0]?.label)}

                {/* Job Type */}
                {detailUI(t('forms.manage_job.job_type'), t('general.UI.' + job.job_type))}

                {/* Salary Range with tooltip */}
                <div className={styles.salaryContainer}>
                    {job.salary_low === 0 && job.salary_high === 0
                        ? detailUI(t('jobs.salary'), t('jobs.market_competitive'))
                        : detailUI(
                            t('jobs.salary'),
                            <div className={styles.salaryWithTooltip}>
                                {`${salary}/${salary_period}`}
                                <span
                                    className={styles.tooltipContainer}
                                    ref={tooltipRef}
                                    onMouseEnter={handleTooltipShow}
                                    onMouseLeave={handleTooltipHide}
                                >
                                    <FaInfoCircle className={styles.infoIcon} />
                                    {showTooltip && (
                                        <div
                                            className={styles.customTooltip}
                                            onMouseEnter={handleTooltipShow}
                                            onMouseLeave={handleTooltipHide}
                                        >
                                            {getSalaryInAllCurrencies()}
                                        </div>
                                    )}
                                </span>
                            </div>
                        )
                    }
                </div>

                {/* Education */}
                {detailUI(t('job_seeker.education'), t(`general.education_levels.${job?.education_level}`))}

                {/* Required Languages */}
                {job.languages && job.languages.length > 0 &&
                    detailUI(
                        t('forms.manage_job.required_languages'),
                        job.languages.map(lang => t(`general.language.${lang.languageCode.toUpperCase()}`)).join(', ')
                    )
                }

                {/* Work Experience */}
                {detailUI(
                    t('talents_list.experience'),
                    job.minworkexp + "-" + job.maxworkexp + " " + (job.maxworkexp > 0 ? t('general.UI.years') : t('general.UI.year'))
                )}

                {/* Required Skills */}
                {job.skills && job.skills.length > 0 &&
                    detailUI(
                        t('job_seeker.general_skills'),
                        <div className={styles.skillsContainer}>
                            <div className={styles.generalSkills}>
                                {(showAllSkills ? job.skills : job.skills.slice(0, 2))?.map((skill, index) => (
                                    <span key={index}>{skill.code}</span>
                                ))}
                                {job.skills.length > 2 && (
                                    <button
                                        className={styles.readMoreBtn}
                                        onClick={() => setShowAllSkills(!showAllSkills)}
                                    >
                                        {!showAllSkills ? `+${job.skills.length - 2} ${t('general.UI.more')}` : t('general.UI.show_less')}
                                    </button>
                                )}
                            </div>
                        </div>
                    )
                }
                {job.role_skills && job.role_skills.length > 0 &&
                    detailUI(
                        t('job_seeker.role_based_skills'),
                        <div className={styles.skillsContainer}>
                            <div className={styles.generalSkills}>
                                {(showAllRoleSkills ? job.role_skills : job.role_skills.slice(0, 2))?.map((skill, index) => (
                                    <span key={index} >{skill.skill_code}</span>
                                ))}
                                {job.role_skills.length > 2 && (
                                    <button
                                        className={styles.readMoreBtn}
                                        onClick={() => setShowAllRoleSkills(!showAllRoleSkills)}
                                    >
                                        {!showAllRoleSkills ? `+${job.role_skills.length - 2} ${t('general.UI.more')}` : t('general.UI.show_less')}
                                    </button>
                                )}
                            </div>
                        </div>
                    )
                }

                {/* Visa Requirement - Conditional */}
                {
                    job.visarequirment == "Yes" &&
                    detailUI(t('forms.manage_job.visa_requirement'), t('general.UI.required'))
                }

                {/* Nationality Requirement - Conditional */}
                {job.nationalityrequirment && job.nationalityrequirment !== 'No' &&
                    detailUI(t('forms.manage_job.nationality'), t(`general.nationality.${job.nationality}`))
                }

                {/* Driving License - Conditional */}
                {job.licenserequire !== 'No' &&
                    detailUI(t('forms.manage_job.driving_license'), t('general.UI.required'))
                }

                {/* Additional Benefits */}
                {job.otherbenefit && job.otherbenefit.length > 0 &&
                    detailUI(
                        t('forms.manage_job.benefits'),
                        <div className={styles.benefitsContainer}>
                            {job.otherbenefit.split(',').map((benefit, index) => (
                                <div key={index} className={styles.benefitItem}>
                                    <FaCheck className={styles.tickMark} />
                                    <span className={styles.benefitText}>{benefit.trim()}</span>
                                </div>
                            ))}
                        </div>
                    )
                }

                {/* Application Deadline */}
                {job?.enddate &&
                    detailUI(
                        t('forms.manage_job.application_deadline'),
                        new Date(job.enddate).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                        })
                    )
                }
            </div>

        </div >
    )
}

export default JobDetailSection;